import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#fdfbf7] ${className}`} data-id="element-282">
      <div className="container mx-auto px-4" data-id="element-283">
        {title && <h2 className="font-bold text-center mb-12 text-[#1a3622] text-[16px] md:text-2xl" data-id="element-284">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-285">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white p-6 rounded-2xl shadow-lg border border-[#e2e8f0] flex flex-col h-full" data-id="element-286">
              <div className="flex items-center gap-1 mb-4" data-id="element-287">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#d4af37] fill-[#d4af37]" data-id="element-288" />)}
              </div>

              <p className="text-[#4a3b32] italic mb-6 flex-grow" data-id="element-289">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#e2e8f0]" data-id="element-290">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-291">
                  {t.initials}
                </div>
                <div data-id="element-292">
                  <p className="font-bold text-[#1a3622] text-sm" data-id="element-293">{t.name}</p>
                  <p className="text-xs text-[#5c4033]" data-id="element-294">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-[#8b5a2b] mt-0.5" data-id="element-295">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'MR',
  name: 'María R.',
  location: 'Docente de Ciencias Agropecuarias — Buenos Aires',
  text: 'Mis alumnos por fin entienden cómo funciona el ciclo productivo bovino y por qué eso importa en la realidad del campo. Antes perdía horas buscando cómo explicarlo con claridad. Ahora entro con todo listo y la clase fluye sola.',
  color: 'bg-[#2c4c3b]'
}, {
  initials: 'JP',
  name: 'Jorge P.',
  location: 'Profesor de Producción Animal — Córdoba',
  text: 'Nunca más llegué a una clase sin saber cómo explicar la diferencia entre fertilización orgánica y química, o cómo presentar el manejo de suelos sin que mis alumnos se pierdan en el primer párrafo. El material hace el trabajo pesado por mí.',
  color: 'bg-[#5c4033]'
}, {
  initials: 'SL',
  name: 'Silvana L.',
  location: 'Docente de Escuela Agrotécnica — Rosario',
  text: 'Las planificaciones me ahorran horas. No improviso nunca más frente a mis alumnos.',
  color: 'bg-[#8b5a2b]'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'VT',
  name: 'Valeria T.',
  location: '🏙️ Buenos Aires',
  text: 'El tomo de producción animal y ganadería le cambió la cara a mis clases. Por fin mis alumnos entienden el ciclo productivo bovino sin confundirse con terminología técnica. Es el recurso más claro que encontré para este tema. Lo uso desde la primera clase y los engancha de inmediato.',
  color: 'bg-[#2c4c3b]',
  pack: 'MegaPack Educación Agropecuaria y Ganadera 360'
}, {
  initials: 'CR',
  name: 'Carlos R.',
  location: '🏙️ Córdoba',
  text: 'El banco de evaluaciones y los libros complementarios valen por sí solos el precio del pack. Nunca más construí un examen desde cero bajo presión. Salgo de cada bimestre con la satisfacción de tener todo resuelto.',
  color: 'bg-[#5c4033]',
  pack: 'MegaPack Educación Agropecuaria y Ganadera 360 + Huerta y Producción Vegetal'
}, {
  initials: 'DG',
  name: 'Daniel G.',
  location: '🏙️ Mendoza',
  text: 'El libro de Proyecto Productivo Final transformó al grupo más difícil que tenía. Mis alumnos conectaron todo lo del año con el mundo real del campo. Eso no lo encontré en ningún otro material.',
  color: 'bg-[#8b5a2b]',
  pack: 'MegaPack Educación Agropecuaria y Ganadera 360'
}, {
  initials: 'AM',
  name: 'Analía M.',
  location: '🏙️ Rosario',
  text: 'El módulo de producción bovina me devolvió la seguridad en el aula. Antes perdía mucho tiempo buscando cómo explicar las diferencias entre sistemas de cría y engorde. Ahora tengo todo organizado y mis alumnos lo entienden al primer intento.',
  color: 'bg-[#1a3622]',
  pack: 'MegaPack Educación Agropecuaria y Ganadera 360'
}, {
  initials: 'SR',
  name: 'Sebastián R.',
  location: '🏙️ La Plata',
  text: 'El libro de Sanidad Animal y el de Bienestar Animal fueron una revelación. Mis alumnos entendieron el calendario de vacunación bovino en una sola clase. Eso cambió completamente el nivel de las discusiones en el aula.',
  color: 'bg-[#2c4c3b]',
  pack: 'MegaPack Educación Agropecuaria y Ganadera 360'
}];